import { Component, HostListener, effect, inject, OnDestroy, signal } from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { ProductsService } from '../../core/services/products.service';
import { SeoService } from '../../core/services/seo.service';
import { TaxonomyService } from '../../core/services/taxonomy.service';
import { formatCondition } from '../../core/utils/currency';
import type {
  AuState,
  Condition,
  FacetBucket,
  Material,
  ProductQueryParams,
  TreeWidth,
} from '../../core/models/product.model';

const TREE_WIDTHS: TreeWidth[] = ['NARROW', 'MEDIUM', 'WIDE', 'EXTRA_WIDE', 'ADJUSTABLE_GULLET'];
const MATERIALS: Material[] = ['LEATHER', 'SYNTHETIC'];
const CONDITIONS: Condition[] = ['EXCELLENT', 'VERY_GOOD', 'GOOD', 'FAIR'];
const AU_STATES: AuState[] = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

@Component({
  selector: 'app-shop',
  standalone: true,
  imports: [NgTemplateOutlet, ProductCardComponent],
  templateUrl: './shop.component.html',
})
export class ShopComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly productsService = inject(ProductsService);
  private readonly taxonomyService = inject(TaxonomyService);
  private readonly seo = inject(SeoService);
  private readonly document = inject(DOCUMENT);

  readonly treeWidths = TREE_WIDTHS;
  readonly materials = MATERIALS;
  readonly conditions = CONDITIONS;
  readonly states = AU_STATES;
  readonly formatCondition = formatCondition;

  readonly mobileFiltersOpen = signal(false);

  readonly query = toSignal(
    this.route.queryParamMap.pipe(map((params) => this.parseQuery(params))),
    { initialValue: this.parseQuery(this.route.snapshot.queryParamMap) },
  );

  // catchError fallbacks matter beyond the browser: /shop is SSR'd per
  // request (see app.routes.server.ts), so an unhandled error here would
  // 500 the page rather than just degrade one section.
  readonly disciplines = toSignal(
    this.taxonomyService.disciplines().pipe(catchError(() => of([]))),
    { initialValue: [] },
  );
  readonly brands = toSignal(this.taxonomyService.brands().pipe(catchError(() => of([]))), {
    initialValue: [],
  });
  readonly colours = toSignal(this.taxonomyService.colours().pipe(catchError(() => of([]))), {
    initialValue: [],
  });
  readonly facets = toSignal(this.productsService.facets().pipe(catchError(() => of(null))), {
    initialValue: null,
  });

  readonly result = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => this.parseQuery(params)),
      switchMap((query) => this.productsService.findAll(query).pipe(catchError(() => of(null)))),
    ),
    { initialValue: null },
  );

  constructor() {
    effect(() => {
      const url = this.result()?.items[0]?.images[0]?.url ?? null;
      this.seo.setLcpPreload(url);
    });
  }

  facetFor(
    field: 'discipline' | 'brand' | 'colour' | 'treeWidth' | 'material' | 'condition' | 'state',
  ): FacetBucket[] {
    return this.facets()?.[field] ?? [];
  }

  countFor(
    field: 'discipline' | 'brand' | 'colour' | 'treeWidth' | 'material' | 'condition' | 'state',
    value: string,
  ): number {
    return this.facetFor(field).find((f) => f.value === value)?.count ?? 0;
  }

  private parseQuery(params: {
    get(name: string): string | null;
  }): ProductQueryParams {
    const csv = (name: string) => params.get(name)?.split(',').filter(Boolean);
    return {
      discipline: csv('discipline'),
      brand: csv('brand'),
      colour: csv('colour'),
      treeWidth: csv('treeWidth') as TreeWidth[] | undefined,
      material: csv('material') as Material[] | undefined,
      condition: csv('condition') as Condition[] | undefined,
      state: csv('state'),
      sort: (params.get('sort') as ProductQueryParams['sort']) ?? undefined,
      page: params.get('page') ? Number(params.get('page')) : 1,
      pageSize: 24,
    };
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.mobileFiltersOpen()) {
      this.closeMobileFilters();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.mobileFiltersOpen() && window.matchMedia('(min-width: 1024px)').matches) {
      this.closeMobileFilters();
    }
  }

  openMobileFilters(): void {
    this.mobileFiltersOpen.set(true);
    this.lockBodyScroll();
  }

  closeMobileFilters(): void {
    this.mobileFiltersOpen.set(false);
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    this.document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    this.document.body.style.overflow = '';
  }

  isActive(field: string, value: string): boolean {
    return !!this.query()[field as keyof ProductQueryParams]
      ?.toString()
      .split(',')
      .includes(value);
  }

  toggleFilter(field: string, value: string): void {
    const current = this.query();
    const existing = ((current[field as keyof ProductQueryParams] as string[]) ?? []).slice();
    const index = existing.indexOf(value);
    if (index >= 0) {
      existing.splice(index, 1);
    } else {
      existing.push(value);
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [field]: existing.length ? existing.join(',') : null, page: null },
      queryParamsHandling: 'merge',
    });
  }

  clearAll(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
  }

  setSort(sort: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort },
      queryParamsHandling: 'merge',
    });
  }

  goToPage(page: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page },
      queryParamsHandling: 'merge',
    });
  }

  totalPages(): number {
    const result = this.result();
    if (!result) return 1;
    return Math.max(1, Math.ceil(result.total / result.pageSize));
  }

  pageNumbers(): number[] {
    return Array.from({ length: this.totalPages() }, (_, i) => i + 1);
  }

  activeFilterEntries(): { field: string; value: string }[] {
    const query = this.query();
    const fields: (keyof ProductQueryParams)[] = [
      'discipline',
      'brand',
      'colour',
      'treeWidth',
      'material',
      'condition',
      'state',
    ];
    return fields.flatMap((field) =>
      ((query[field] as string[]) ?? []).map((value) => ({ field, value })),
    );
  }
}
